import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./AdminOperations.css";
import { apiSubirExcel, routeFindUserByRol } from "./constants";

export const UploadExcel = () => {
  const [archivo, setArchivo] = useState(null);
  const [matriculaAdmin, setMatriculaAdmin] = useState("");
  const [administradores, setAdministradores] = useState([]);
  const [cargandoAdministradores, setCargandoAdministradores] = useState(true);
  const [errorAdministradores, setErrorAdministradores] = useState("");
  const [resultado, setResultado] = useState(null);
  const [estadoCarga, setEstadoCarga] = useState(null);
  const [mensajeCarga, setMensajeCarga] = useState("");
  const [porcentajeCarga, setPorcentajeCarga] = useState(0);
  const inputArchivo = useRef(null);
  const navigate = useNavigate();
  const estaSubiendo = estadoCarga === "subiendo";

  useEffect(() => {
    const fetchAdministradores = async () => {
      try {
        const response = await axios.get(routeFindUserByRol, {
          params: { role: "Admin" },
        });
        const lista = response.data?.data ?? response.data ?? [];
        setAdministradores(Array.isArray(lista) ? lista : []);
      } catch (error) {
        console.error("Error al cargar administradores:", error);
        setErrorAdministradores(
          "No fue posible cargar la lista de administradores.",
        );
      } finally {
        setCargandoAdministradores(false);
      }
    };

    fetchAdministradores();
  }, []);

  const handleArchivoChange = (event) => {
    setArchivo(event.target.files[0] || null);
    setResultado(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setResultado(null);

    if (!archivo || !matriculaAdmin) {
      setResultado({
        tipo: "warning",
        mensaje: "Selecciona un administrador y un archivo Excel.",
      });
      return;
    }

    setEstadoCarga("subiendo");
    setMensajeCarga("Subiendo el archivo Excel...");
    setPorcentajeCarga(0);

    try {
      const formData = new FormData();
      formData.append("archivo", archivo);
      formData.append("matricula_admin", matriculaAdmin);

      const response = await axios.post(apiSubirExcel, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) {
            return;
          }

          const porcentaje = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          setPorcentajeCarga(porcentaje);
          setMensajeCarga(
            porcentaje === 100
              ? "Archivo enviado. Procesando en el servidor..."
              : "Subiendo el archivo Excel...",
          );
        },
      });

      if (response.data?.success === false) {
        setEstadoCarga("error");
        setMensajeCarga(
          response.data.message || "No se pudo subir el archivo Excel.",
        );
        return;
      }

      setEstadoCarga("exito");
      setMensajeCarga(
        response.data?.message || "El archivo Excel se subió correctamente.",
      );
      setArchivo(null);
      if (inputArchivo.current) {
        inputArchivo.current.value = "";
      }
    } catch (error) {
      console.error("Error al subir el archivo Excel:", error);
      setEstadoCarga("error");
      setMensajeCarga(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "No se pudo subir el archivo Excel.",
      );
    }
  };

  const handleCerrarResultado = () => {
    if (!estaSubiendo) {
      setEstadoCarga(null);
      setMensajeCarga("");
    }
  };

  return (
    <div className="admin-operation-page">
      <main className="admin-operation-container admin-operation-container-narrow">
        <header className="admin-operation-header">
          <p className="admin-operation-eyebrow">Administración de archivos</p>
          <h1>Subir archivo Excel</h1>
          <p>
            Selecciona al administrador responsable y carga un archivo XLS o
            XLSX para conservarlo en la plataforma.
          </p>
        </header>

        <form className="admin-operation-panel" onSubmit={handleSubmit}>
          <div className="admin-operation-grid">
            <section className="admin-operation-card">
              <div className="admin-operation-card-heading">
                <span>1</span>
                <div>
                  <h2>Responsable</h2>
                  <p>Identifica quién realiza la carga.</p>
                </div>
              </div>
              <div className="admin-operation-field">
                <label htmlFor="excel-admin">Administrador</label>
                <select
                  id="excel-admin"
                  value={matriculaAdmin}
                  onChange={(event) => setMatriculaAdmin(event.target.value)}
                  disabled={cargandoAdministradores || estaSubiendo}
                  required
                >
                  <option value="">
                    {cargandoAdministradores
                      ? "Cargando administradores..."
                      : "Selecciona un administrador"}
                  </option>
                  {administradores.map((administrador) => (
                    <option
                      key={administrador.id ?? administrador.matricula}
                      value={administrador.matricula}
                    >
                      {administrador.matricula} - {administrador.nombre}{" "}
                      {administrador.apellido}
                    </option>
                  ))}
                </select>
                {errorAdministradores && <small>{errorAdministradores}</small>}
                {!cargandoAdministradores &&
                  !errorAdministradores &&
                  administradores.length === 0 && (
                    <small>No hay administradores registrados.</small>
                  )}
              </div>
            </section>

            <section className="admin-operation-card">
              <div className="admin-operation-card-heading">
                <span>2</span>
                <div>
                  <h2>Archivo</h2>
                  <p>Formatos permitidos: .xls y .xlsx.</p>
                </div>
              </div>
              <div className="admin-operation-field">
                <label htmlFor="excel-file">Selecciona el archivo</label>
                <input
                  ref={inputArchivo}
                  className="admin-operation-file"
                  type="file"
                  id="excel-file"
                  name="archivo"
                  accept=".xlsx,.xls"
                  onChange={handleArchivoChange}
                  disabled={estaSubiendo}
                  required
                />
                {archivo && <small>Archivo seleccionado: {archivo.name}</small>}
              </div>
            </section>
          </div>

          {resultado && (
            <div
              className={`admin-operation-status admin-operation-status-${resultado.tipo}`}
              role="status"
            >
              {resultado.mensaje}
            </div>
          )}

          <div className="admin-operation-actions">
            <button
              className="admin-operation-button"
              type="submit"
              disabled={
                cargandoAdministradores ||
                administradores.length === 0 ||
                estaSubiendo
              }
            >
              {estaSubiendo ? "Subiendo archivo..." : "Subir archivo"}
            </button>
            <button
              className="admin-operation-button admin-operation-button-secondary"
              type="button"
              onClick={() => navigate("/admin-home")}
              disabled={estaSubiendo}
            >
              Regresar al inicio
            </button>
          </div>
        </form>
      </main>

      {estadoCarga && (
        <div className="admin-operation-modal-overlay" role="presentation">
          <div
            className="admin-operation-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="upload-status-title"
          >
            <h2 id="upload-status-title">
              {estadoCarga === "subiendo" && "Carga en proceso"}
              {estadoCarga === "exito" && "Carga completada"}
              {estadoCarga === "error" && "No se pudo completar la carga"}
            </h2>
            <p>{mensajeCarga}</p>

            {estaSubiendo && (
              <>
                <div className="admin-operation-progress">
                  <div style={{ width: `${porcentajeCarga}%` }} />
                </div>
                <p>
                  {porcentajeCarga > 0
                    ? `${porcentajeCarga}% enviado`
                    : "Preparando archivo..."}
                </p>
              </>
            )}

            {!estaSubiendo && (
              <button
                className="admin-operation-button"
                type="button"
                onClick={handleCerrarResultado}
              >
                Cerrar
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
